import { FormikErrors, FormikHelpers, useFormik } from 'formik';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import React, { useEffect, useState } from 'react';
import wasteTypesService from '../../service/api/WasteTypesService';
import FormErrorMessage from '../_shared/FormErrorMessage';
import FormErrorMessageScroller from '../_shared/FormErrorMessageScroller';
import { useTranslation } from 'react-i18next';
import { WasteGroup } from '../../types/WasteGroup';
import { WasteType } from '../../types/WasteType';

interface FormValuesProps extends WasteType {}

const INIT_FORM_STATE: FormValuesProps = {
  id: null,
  name: '',
  code: '',
  limit: null,
  wasteGroupId: null,
};

type Props = {
  dialogVisible: boolean;
  closeAddEditModal: VoidFunction;
  wasteType: WasteType;
  availableWasteGroups: WasteGroup[];
};

function WasteTypeDialog({
  dialogVisible,
  closeAddEditModal,
  wasteType,

  availableWasteGroups,
}: Props) {
  const [initFormValues, setInitFormValues] = useState(INIT_FORM_STATE);

  const hideDialog = () => {
    formik.resetForm({ values: INIT_FORM_STATE });
    closeAddEditModal();
  };

  const wasteGroups = availableWasteGroups?.map((r) => {
    return { label: r.name, value: r.id };
  });

  const { t } = useTranslation();

  useEffect(() => {
    if (dialogVisible && wasteType) {
      setInitFormValues({
        id: wasteType.id,
        name: wasteType.name,
        code: wasteType.code,
        limit: wasteType.limit,
        wasteGroupId: wasteType.wasteGroupId,
      });
    } else {
      setInitFormValues(INIT_FORM_STATE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogVisible]);

  const formik = useFormik({
    initialValues: initFormValues,
    validate: (data: FormValuesProps) => {
      let errors: FormikErrors<FormValuesProps> = {};

      if (!data.name?.trim()) {
        errors.name = t('common.error_name_required');
      }
      if (!data.code?.trim()) {
        errors.code = t('wasteTypes.error_code_required');
      }
      if (!data.wasteGroupId) {
        errors.wasteGroupId = t('wasteTypes.error_waste_group_required');
      }
      return errors;
    },
    onSubmit: async (formData: FormValuesProps, helpers: FormikHelpers<FormValuesProps>) => {
      try {
        if (!formData.id) {
          await wasteTypesService.create(formData);
        } else {
          await wasteTypesService.edit(formData, formData.id);
        }
        hideDialog();
      } catch (error: any) {
        if (error.response.status === 409) {
          if (error.response.data.field === 'code') {
            helpers.setFieldError('code', t('wasteTypes.error_code_already_taken'));
          } else {
            helpers.setFieldError('name', t('wasteTypes.error_name_already_taken'));
          }
        }
      } finally {
        helpers.setSubmitting(false);
      }
    },
    enableReinitialize: true,
  });

  const DialogFooter = (
    <>
      <Button
        type="reset"
        label={t('common.cancel')}
        icon="pi pi-times"
        className="p-button-text"
        onClick={hideDialog}
      />
      <Button
        type="submit"
        label={t('common.save')}
        icon="pi pi-check"
        className="p-button-text"
        onClick={formik.submitForm}
      />
    </>
  );

  return (
    <FormErrorMessageScroller formikInstance={formik}>
      <Dialog
        visible={dialogVisible}
        header={t('wasteTypes.waste_type_details')}
        modal
        className="p-fluid"
        footer={DialogFooter}
        onHide={hideDialog}
        breakpoints={{ '1400px': '60vw', '896px': '90vw' }}
        style={{ width: '40vw' }}
      >
        <form>
          <div className="col-10">
            <div className="field">
              <label htmlFor="name">{t('common.name')}</label>
              <InputText
                id="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                className={formik.touched.name && formik.errors.name && 'p-invalid'}
              />
              <FormErrorMessage fieldName="name" formikInstance={formik} />
            </div>

            <div className="field">
              <label htmlFor="code">{t('wasteTypes.code')}</label>
              <InputText
                id="code"
                value={formik.values.code}
                onChange={formik.handleChange}
                className={formik.touched.code && formik.errors.code && 'p-invalid'}
              />
              <FormErrorMessage fieldName="code" formikInstance={formik} />
            </div>

            <div className="field">
              <label htmlFor="limit">{t('wasteTypes.limit')}</label>
              <InputNumber
                id="limit"
                min={1}
                max={1000000}
                value={formik.values.limit}
                onValueChange={formik.handleChange}
                className={formik.touched.limit && formik.errors.limit && 'p-invalid'}
              />
              <FormErrorMessage fieldName="limit" formikInstance={formik} />
            </div>

            <div className="field">
              <label htmlFor="wasteGroupId">{t('wasteTypes.waste_group')}</label>
              <Dropdown
                id="wasteGroupId"
                value={formik.values.wasteGroupId}
                onChange={formik.handleChange}
                options={wasteGroups}
                placeholder={t('common.select')}
                emptyMessage={t('wasteTypes.waste_groups_empty_message')}
                className={formik.touched.wasteGroupId && formik.errors.wasteGroupId && 'p-invalid'}
              />
              <FormErrorMessage fieldName="wasteGroupId" formikInstance={formik} />
            </div>
          </div>
        </form>
      </Dialog>
    </FormErrorMessageScroller>
  );
}

export default WasteTypeDialog;
